import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];
const AUDIO_INPUT_STORAGE_KEY = "voice.selectedAudioInputId";
const AUDIO_OUTPUT_STORAGE_KEY = "voice.selectedAudioOutputId";
const NOISE_SUPPRESSION_STORAGE_KEY = "voice.noiseSuppression";
const AUTO_GAIN_STORAGE_KEY = "voice.autoGainControl";
const ANTI_ECHO_STORAGE_KEY = "voice.antiEchoGate";
const CHAT_READ_STORAGE_PREFIX = "voice.chatReadAt.";
const VOICE_GATE_THRESHOLD = 0.014;
const VOICE_GATE_SOFT_OPEN_THRESHOLD = 0.01;
const VOICE_GATE_CLOSE_THRESHOLD = 0.006;
const VOICE_GATE_RELEASE_MS = 1200;
const DUCKING_VOLUME_FACTOR = 0.42;
const CLOSED_GATE_GAIN = 0.16;
const OPEN_GATE_TIME_CONSTANT = 0.008;
const CLOSE_GATE_TIME_CONSTANT = 0.22;
const OUTBOUND_VOICE_GAIN = 1.45;
const MAX_CHAT_DRAFT_LENGTH = 1200;

function readStoredBoolean(key, fallback) {
  if (typeof window === "undefined") return fallback;
  const value = window.localStorage.getItem(key);
  if (value === null) return fallback;
  return value === "1";
}

function writeStoredBoolean(key, value) {
  try {
    window.localStorage.setItem(key, value ? "1" : "0");
  } catch {
    // Local storage can be unavailable in private or restricted contexts.
  }
}

function getChatReadStorageKey(channelId) {
  return `${CHAT_READ_STORAGE_PREFIX}${encodeURIComponent(String(channelId || ""))}`;
}

function readStoredChatReadAt(channelId) {
  if (typeof window === "undefined" || !channelId) return 0;
  const value = Number(window.localStorage.getItem(getChatReadStorageKey(channelId)) || 0);
  return Number.isFinite(value) ? value : 0;
}

function writeStoredChatReadAt(channelId, value) {
  if (typeof window === "undefined" || !channelId) return;
  try {
    window.localStorage.setItem(getChatReadStorageKey(channelId), String(Math.max(0, Number(value || 0))));
  } catch {
    // Local storage can be unavailable in private or restricted contexts.
  }
}

function emitAck(socket, event, payload = {}, timeoutMs = 8000) {
  return new Promise((resolve) => {
    if (!socket?.emit) return resolve({ ok: false, error: "socket_unavailable" });
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve({ ok: false, error: "ack_timeout" });
    }, timeoutMs);

    socket.emit(event, payload, (response) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(response || { ok: true });
    });
  });
}

function makePeerConnection({ socket, channelId, targetId, localStream, onRemoteStream, onPeerClosed }) {
  const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

  localStream?.getTracks?.().forEach((track) => {
    pc.addTrack(track, localStream);
  });

  pc.onicecandidate = (event) => {
    if (!event.candidate) return;
    socket.emit("VOICE:ICE", {
      channelId,
      targetId,
      candidate: event.candidate,
    });
  };

  pc.ontrack = (event) => {
    const [stream] = event.streams || [];
    if (stream) onRemoteStream(targetId, stream);
  };

  pc.onconnectionstatechange = () => {
    if (["failed", "closed", "disconnected"].includes(pc.connectionState)) {
      onPeerClosed(targetId);
    }
  };

  return pc;
}

function enableOpusDtx(description) {
  if (!description?.sdp || !description?.type) return description;
  const lines = description.sdp.split("\r\n");
  const opusRtpMap = lines.find((line) => /^a=rtpmap:\d+ opus\/48000/i.test(line));
  const opusPayload = opusRtpMap?.match(/^a=rtpmap:(\d+)/)?.[1];
  if (!opusPayload) return description;

  const fmtpIndex = lines.findIndex((line) => line.startsWith(`a=fmtp:${opusPayload} `));
  if (fmtpIndex >= 0) {
    if (!/usedtx=1/i.test(lines[fmtpIndex])) {
      lines[fmtpIndex] = `${lines[fmtpIndex]};usedtx=1`;
    }
  } else {
    const rtpmapIndex = lines.findIndex((line) => line === opusRtpMap);
    lines.splice(rtpmapIndex + 1, 0, `a=fmtp:${opusPayload} usedtx=1`);
  }

  return new RTCSessionDescription({
    type: description.type,
    sdp: lines.join("\r\n"),
  });
}

function IconMic({ off = false }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V7a3 3 0 0 0-3-3Z" fill="none" stroke="currentColor" strokeWidth="1.9" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3M8.5 21h7" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      {off ? <path d="M4 4l16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /> : null}
    </svg>
  );
}

function IconAudio({ off = false }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 9v6h4l5 4V5L9 9H5Z" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
      {off ? (
        <path d="M18 9l3 3m0-3-3 3M4 4l16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      ) : (
        <path d="M17 9.5a4 4 0 0 1 0 5M19.5 7a7.5 7.5 0 0 1 0 10" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      )}
    </svg>
  );
}

function IconPhone({ hangup = false }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d={hangup ? "M5 15c4-3 10-3 14 0l-2.2 3-3.2-1.3h-3.2L7.2 18 5 15Z" : "M7 5c1 6 6 11 12 12l-2 3c-8-1.5-13.5-7-15-15l3-2 2 2Z"}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconMinimize() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 12h10" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" />
    </svg>
  );
}

function IconGear() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 8.2a3.8 3.8 0 1 0 0 7.6 3.8 3.8 0 0 0 0-7.6ZM19 12a7.6 7.6 0 0 0-.1-1.1l2-1.5-2-3.4-2.4 1a8.1 8.1 0 0 0-1.9-1.1L14.3 3h-4.6l-.3 2.9A8.1 8.1 0 0 0 7.5 7l-2.4-1-2 3.4 2 1.5A7.6 7.6 0 0 0 5 12c0 .4 0 .8.1 1.1l-2 1.5 2 3.4 2.4-1a8.1 8.1 0 0 0 1.9 1.1l.3 2.9h4.6l.3-2.9a8.1 8.1 0 0 0 1.9-1.1l2.4 1 2-3.4-2-1.5c.1-.3.1-.7.1-1.1Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function getDisplayName(participant) {
  return String(participant?.name || participant?.email || "Uczestnik").trim() || "Uczestnik";
}

function formatChatTime(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();
  const time = date.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
  if (isToday) return time;
  if (isYesterday) return `wczoraj ${time}`;
  return `${date.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit" })} ${time}`;
}

function formatChatTimestampTitle(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("pl-PL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function chatMessageTimeMs(message) {
  const time = message?.createdAt ? new Date(message.createdAt).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
}

function stopControlPointerEvent(event) {
  event.stopPropagation();
}

function IconButton({ title, active = false, danger = false, disabled = false, onClick, children }) {
  return (
    <button
      type="button"
      className={`btn ${active ? "btn-primary" : "btn-secondary"}`}
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      onPointerDown={stopControlPointerEvent}
      onTouchStart={stopControlPointerEvent}
      onContextMenu={(event) => event.preventDefault()}
      style={{
        width: 34,
        height: 34,
        minWidth: 34,
        padding: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: danger ? "#fecaca" : undefined,
        touchAction: "manipulation",
      }}
    >
      {children}
    </button>
  );
}

export default function VoiceChannel({
  channelId,
  socket,
  currentUser = null,
  compact = false,
  dockable = false,
  themeMode = "dark",
  title = "Głos",
}) {
  const [joined, setJoined] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [panelMode, setPanelMode] = useState("button");
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState("");
  const [muted, setMuted] = useState(false);
  const [deafened, setDeafened] = useState(false);
  const [volume, setVolume] = useState(1);
  const [micLevel, setMicLevel] = useState(0);
  const [dockOffset, setDockOffset] = useState(0);
  const [audioInputs, setAudioInputs] = useState([]);
  const [audioOutputs, setAudioOutputs] = useState([]);
  const [selectedAudioInputId, setSelectedAudioInputId] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(AUDIO_INPUT_STORAGE_KEY) || "";
  });
  const [selectedAudioOutputId, setSelectedAudioOutputId] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(AUDIO_OUTPUT_STORAGE_KEY) || "";
  });
  const [currentMicLabel, setCurrentMicLabel] = useState("");
  const [currentAudioSettings, setCurrentAudioSettings] = useState({});
  const [noiseSuppressionEnabled] = useState(() => readStoredBoolean(NOISE_SUPPRESSION_STORAGE_KEY, true));
  const [autoGainControlEnabled] = useState(() => readStoredBoolean(AUTO_GAIN_STORAGE_KEY, true));
  const [antiEchoGateEnabled] = useState(() => readStoredBoolean(ANTI_ECHO_STORAGE_KEY, true));
  const [localVoiceActive, setLocalVoiceActive] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [chatMessages, setChatMessages] = useState([]);
  const [chatDraft, setChatDraft] = useState("");
  const [chatError, setChatError] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [lastChatReadAt, setLastChatReadAt] = useState(() => readStoredChatReadAt(channelId));

  const rawLocalStreamRef = useRef(null);
  const localStreamRef = useRef(null);
  const outboundProcessorRef = useRef(null);
  const peersRef = useRef(new Map());
  const audioNodesRef = useRef(new Map());
  const joinedRef = useRef(false);
  const mutedRef = useRef(false);
  const antiEchoGateRef = useRef(antiEchoGateEnabled);
  const localVoiceActiveRef = useRef(false);
  const lastVoiceAtRef = useRef(0);
  const channelIdRef = useRef(channelId);
  const audioMeterRef = useRef({ context: null, source: null, analyser: null, rafId: null });
  const panelRef = useRef(null);
  const chatListRef = useRef(null);

  useEffect(() => {
    channelIdRef.current = channelId;
    setLastChatReadAt(readStoredChatReadAt(channelId));
    setUnreadChatCount(0);
  }, [channelId]);

  useEffect(() => {
    mutedRef.current = muted;
    applyOutboundAudioState();
  }, [muted]);

  useEffect(() => {
    antiEchoGateRef.current = antiEchoGateEnabled;
    writeStoredBoolean(ANTI_ECHO_STORAGE_KEY, antiEchoGateEnabled);
    applyOutboundAudioState();
  }, [antiEchoGateEnabled]);

  const selfParticipant = useMemo(
    () => participants.find((participant) => participant.socketId === socket?.id) || null,
    [participants, socket?.id]
  );

  const remoteParticipants = useMemo(
    () => participants.filter((participant) => participant.socketId !== socket?.id),
    [participants, socket?.id]
  );

  const selfName = getDisplayName(selfParticipant || currentUser);
  const statusText = joined
    ? `${participants.length || 1} na kanale`
    : connecting
      ? "Łączenie..."
      : "Poza kanałem";
  const volumePercent = Math.round(volume * 100);
  const micLevelPercent = Math.max(0, Math.min(100, Math.round(micLevel * 100)));
  const selfUserId = String(currentUser?._id || currentUser?.id || selfParticipant?.userId || "");
  const isLightTheme = themeMode === "light";
  const panelColors = {
    panelBg: isLightTheme ? "rgba(255,255,255,0.94)" : "rgba(2,6,23,0.84)",
    panelText: isLightTheme ? "#0f172a" : "#f8fafc",
    mutedText: isLightTheme ? "rgba(51,65,85,0.76)" : "rgba(226,232,240,0.82)",
    faintText: isLightTheme ? "rgba(71,85,105,0.62)" : "rgba(226,232,240,0.58)",
    border: isLightTheme ? "rgba(15,23,42,0.18)" : "rgba(148,163,184,0.45)",
    softBorder: isLightTheme ? "rgba(15,23,42,0.13)" : "rgba(148,163,184,0.22)",
    controlBg: isLightTheme ? "rgba(248,250,252,0.96)" : "rgba(15,23,42,0.92)",
    chatBg: isLightTheme ? "rgba(241,245,249,0.82)" : "rgba(15,23,42,0.46)",
    pillBg: isLightTheme ? "rgba(226,232,240,0.78)" : "rgba(15,23,42,0.68)",
    strong: isLightTheme ? "#0369a1" : "#bae6fd",
    error: isLightTheme ? "#b91c1c" : "#fca5a5",
    gripBg: isLightTheme ? "rgba(248,250,252,0.96)" : "rgba(2,6,23,0.82)",
  };
  const themedInputSelectStyle = getInputSelectStyle(isLightTheme);
  const themedParticipantPillStyle = getParticipantPillStyle(isLightTheme);
  const sortedChatMessages = useMemo(
    () => [...chatMessages].sort((a, b) => new Date(a?.createdAt || 0).getTime() - new Date(b?.createdAt || 0).getTime()),
    [chatMessages]
  );
  const unreadBadgeLabel = unreadChatCount > 99 ? "99+" : String(unreadChatCount);

  async function refreshAudioInputs() {
    if (!navigator?.mediaDevices?.enumerateDevices) return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices
        .filter((device) => device.kind === "audioinput")
        .map((device, index) => ({
          deviceId: String(device.deviceId || ""),
          label: String(device.label || `Mikrofon ${index + 1}`),
        }));
      const outputs = devices
        .filter((device) => device.kind === "audiooutput")
        .map((device, index) => ({
          deviceId: String(device.deviceId || ""),
          label: String(device.label || `Wyjście ${index + 1}`),
        }));
      setAudioInputs(inputs);
      setAudioOutputs(outputs);
    } catch {
      // Device enumeration can fail when permissions are blocked.
    }
  }

  function buildAudioConstraints(deviceId = selectedAudioInputId, options = {}) {
    const audio = {
      echoCancellation: true,
      noiseSuppression: options.noiseSuppression ?? noiseSuppressionEnabled,
      autoGainControl: options.autoGainControl ?? autoGainControlEnabled,
      channelCount: { ideal: 1 },
    };
    if (deviceId) {
      audio.deviceId = { exact: deviceId };
    }
    return { audio, video: false };
  }

  function rememberAudioInput(deviceId) {
    setSelectedAudioInputId(deviceId);
    try {
      if (deviceId) {
        window.localStorage.setItem(AUDIO_INPUT_STORAGE_KEY, deviceId);
      } else {
        window.localStorage.removeItem(AUDIO_INPUT_STORAGE_KEY);
      }
    } catch {
      // Local storage can be unavailable in private or restricted contexts.
    }
  }

  function rememberAudioOutput(deviceId) {
    setSelectedAudioOutputId(deviceId);
    try {
      if (deviceId) {
        window.localStorage.setItem(AUDIO_OUTPUT_STORAGE_KEY, deviceId);
      } else {
        window.localStorage.removeItem(AUDIO_OUTPUT_STORAGE_KEY);
      }
    } catch {
      // Local storage can be unavailable in private or restricted contexts.
    }
  }

  function updateCurrentMicInfo(stream) {
    const [track] = stream?.getAudioTracks?.() || [];
    const settings = track?.getSettings?.() || {};
    setCurrentAudioSettings(settings);
    setCurrentMicLabel(track?.label || "Domyślny mikrofon");
  }

  async function openLocalAudioStream(deviceId = selectedAudioInputId, options = {}) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(buildAudioConstraints(deviceId, options));
      updateCurrentMicInfo(stream);
      await refreshAudioInputs();
      return stream;
    } catch (error) {
      if (!deviceId || error?.name === "NotAllowedError") throw error;
      const stream = await navigator.mediaDevices.getUserMedia(buildAudioConstraints("", options));
      rememberAudioInput("");
      updateCurrentMicInfo(stream);
      await refreshAudioInputs();
      return stream;
    }
  }

  function closeOutboundProcessor(processor = outboundProcessorRef.current) {
    if (!processor) return;
    try {
      processor.source?.disconnect?.();
      processor.highPass?.disconnect?.();
      processor.compressor?.disconnect?.();
      processor.voiceGain?.disconnect?.();
      processor.gateGain?.disconnect?.();
      processor.context?.close?.();
    } catch {
      // ignore
    }
    if (processor === outboundProcessorRef.current) {
      outboundProcessorRef.current = null;
    }
  }

  function createOutboundStream(rawStream) {
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor || !rawStream) {
      const fallbackStream = new MediaStream();
      rawStream?.getAudioTracks?.().forEach((track) => {
        fallbackStream.addTrack(track.clone());
      });
      return { stream: fallbackStream, processor: null };
    }

    try {
      const context = new AudioContextCtor();
      const source = context.createMediaStreamSource(rawStream);
      const highPass = context.createBiquadFilter();
      highPass.type = "highpass";
      highPass.frequency.value = 85;
      highPass.Q.value = 0.65;

      const compressor = context.createDynamicsCompressor();
      compressor.threshold.value = -30;
      compressor.knee.value = 18;
      compressor.ratio.value = 4;
      compressor.attack.value = 0.006;
      compressor.release.value = 0.18;

      const voiceGain = context.createGain();
      voiceGain.gain.value = OUTBOUND_VOICE_GAIN;

      const gateGain = context.createGain();
      gateGain.gain.value = antiEchoGateRef.current ? CLOSED_GATE_GAIN : 1;

      const destination = context.createMediaStreamDestination();
      source.connect(highPass);
      highPass.connect(compressor);
      compressor.connect(voiceGain);
      voiceGain.connect(gateGain);
      gateGain.connect(destination);

      const [track] = destination.stream.getAudioTracks();
      if (track) track.contentHint = "speech";

      return {
        stream: destination.stream,
        processor: { context, source, highPass, compressor, voiceGain, gateGain, destination },
      };
    } catch {
      const fallbackStream = new MediaStream();
      rawStream?.getAudioTracks?.().forEach((track) => {
        fallbackStream.addTrack(track.clone());
      });
      return { stream: fallbackStream, processor: null };
    }
  }

  function applyOutboundAudioState(forceVoiceActive = localVoiceActiveRef.current) {
    const shouldSendAudio = !mutedRef.current && (!antiEchoGateRef.current || forceVoiceActive);
    const processor = outboundProcessorRef.current;
    if (processor?.gateGain?.gain && processor?.context) {
      const now = processor.context.currentTime;
      const targetGain = mutedRef.current ? 0 : shouldSendAudio ? 1 : CLOSED_GATE_GAIN;
      const timeConstant = shouldSendAudio ? OPEN_GATE_TIME_CONSTANT : CLOSE_GATE_TIME_CONSTANT;
      processor.gateGain.gain.cancelScheduledValues(now);
      processor.gateGain.gain.setTargetAtTime(targetGain, now, timeConstant);
      localStreamRef.current?.getAudioTracks?.().forEach((track) => {
        track.enabled = !mutedRef.current;
      });
      return;
    }
    localStreamRef.current?.getAudioTracks?.().forEach((track) => {
      track.enabled = shouldSendAudio;
    });
  }

  function setLocalVoiceState(active) {
    if (localVoiceActiveRef.current === active) return;
    localVoiceActiveRef.current = active;
    setLocalVoiceActive(active);
    applyOutboundAudioState(active);
  }

  async function replaceLocalAudioInput(deviceId, options = {}) {
    const rawStream = await openLocalAudioStream(deviceId, options);
    const { stream, processor } = createOutboundStream(rawStream);

    const [nextTrack] = stream.getAudioTracks();
    const previousRawStream = rawLocalStreamRef.current;
    const previousStream = localStreamRef.current;
    const previousProcessor = outboundProcessorRef.current;
    rawLocalStreamRef.current = rawStream;
    localStreamRef.current = stream;
    outboundProcessorRef.current = processor;
    applyOutboundAudioState(localVoiceActiveRef.current);
    startMicMeter(rawStream);

    peersRef.current.forEach((pc) => {
      const sender = pc.getSenders?.().find((item) => item.track?.kind === "audio");
      if (sender && nextTrack) {
        sender.replaceTrack(nextTrack);
      } else if (nextTrack) {
        pc.addTrack(nextTrack, stream);
      }
    });

    previousRawStream?.getTracks?.().forEach((track) => track.stop());
    previousStream?.getTracks?.().forEach((track) => track.stop());
    closeOutboundProcessor(previousProcessor);
  }

  function closePeer(peerId) {
    const pc = peersRef.current.get(peerId);
    if (pc) {
      try {
        pc.close();
      } catch {
        // ignore
      }
    }
    peersRef.current.delete(peerId);
    setRemoteStreams((current) => {
      const next = { ...current };
      delete next[peerId];
      return next;
    });
  }

  function closeAllPeers() {
    Array.from(peersRef.current.keys()).forEach(closePeer);
  }

  function stopMicMeter() {
    const meter = audioMeterRef.current;
    if (meter.rafId) cancelAnimationFrame(meter.rafId);
    try {
      meter.source?.disconnect?.();
      meter.context?.close?.();
    } catch {
      // ignore
    }
    audioMeterRef.current = { context: null, source: null, analyser: null, rafId: null };
    setMicLevel(0);
  }

  function startMicMeter(stream) {
    stopMicMeter();
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor || !stream) return;
    try {
      const context = new AudioContextCtor();
      const analyser = context.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.78;
      const source = context.createMediaStreamSource(stream);
      source.connect(analyser);
      const data = new Uint8Array(analyser.fftSize);

      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) {
          const centered = (data[i] - 128) / 128;
          sum += centered * centered;
        }
        const rms = Math.sqrt(sum / data.length);
        setMicLevel(Math.min(1, rms * 7));
        if (joinedRef.current && !mutedRef.current && antiEchoGateRef.current) {
          const now = performance.now();
          const shouldOpenGate =
            rms >= VOICE_GATE_THRESHOLD ||
            (!localVoiceActiveRef.current && rms >= VOICE_GATE_SOFT_OPEN_THRESHOLD);
          if (shouldOpenGate) {
            lastVoiceAtRef.current = now;
            setLocalVoiceState(true);
          } else if (rms > VOICE_GATE_CLOSE_THRESHOLD) {
            lastVoiceAtRef.current = now;
          } else if (localVoiceActiveRef.current && now - lastVoiceAtRef.current > VOICE_GATE_RELEASE_MS) {
            setLocalVoiceState(false);
          }
        } else if (!antiEchoGateRef.current && localStreamRef.current && !localVoiceActiveRef.current) {
          setLocalVoiceState(true);
        }
        audioMeterRef.current.rafId = requestAnimationFrame(tick);
      };

      audioMeterRef.current = { context, source, analyser, rafId: requestAnimationFrame(tick) };
    } catch {
      stopMicMeter();
    }
  }

  function stopLocalStream() {
    stopMicMeter();
    rawLocalStreamRef.current?.getTracks?.().forEach((track) => track.stop());
    rawLocalStreamRef.current = null;
    localStreamRef.current?.getTracks?.().forEach((track) => track.stop());
    localStreamRef.current = null;
    closeOutboundProcessor();
    setLocalVoiceState(false);
    lastVoiceAtRef.current = 0;
    setCurrentMicLabel("");
    setCurrentAudioSettings({});
  }

  function getOrCreatePeer(peerId) {
    const existing = peersRef.current.get(peerId);
    if (existing) return existing;
    const pc = makePeerConnection({
      socket,
      channelId: channelIdRef.current,
      targetId: peerId,
      localStream: localStreamRef.current,
      onRemoteStream: (id, stream) => {
        setRemoteStreams((current) => ({ ...current, [id]: stream }));
      },
      onPeerClosed: closePeer,
    });
    peersRef.current.set(peerId, pc);
    return pc;
  }

  async function createOfferFor(peerId) {
    const pc = getOrCreatePeer(peerId);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(enableOpusDtx(offer));
    socket.emit("VOICE:OFFER", {
      channelId: channelIdRef.current,
      targetId: peerId,
      description: pc.localDescription,
    });
  }

  async function leaveChannel() {
    const activeChannelId = channelIdRef.current;
    joinedRef.current = false;
    setJoined(false);
    setConnecting(false);
    setError("");
    closeAllPeers();
    stopLocalStream();
    setParticipants([]);
    setRemoteStreams({});
    await emitAck(socket, "VOICE:LEAVE", { channelId: activeChannelId }, 2500);
  }

  async function joinChannel() {
    if (!channelId || !socket?.connected || connecting || joinedRef.current) return;
    if (!navigator?.mediaDevices?.getUserMedia || typeof RTCPeerConnection === "undefined") {
      setError("Ta przeglądarka nie obsługuje połączeń głosowych.");
      return;
    }

    setConnecting(true);
    setError("");

    try {
      const rawStream = await openLocalAudioStream();
      const { stream, processor } = createOutboundStream(rawStream);
      rawLocalStreamRef.current = rawStream;
      localStreamRef.current = stream;
      outboundProcessorRef.current = processor;
      lastVoiceAtRef.current = performance.now();
      setLocalVoiceState(!antiEchoGateRef.current);
      applyOutboundAudioState(!antiEchoGateRef.current);
      startMicMeter(rawStream);

      const response = await emitAck(socket, "VOICE:JOIN", {
        channelId,
        muted,
        deafened,
      });

      if (!response?.ok) {
        throw new Error(response?.error || "voice_join_failed");
      }

      setParticipants(Array.isArray(response.participants) ? response.participants : []);
      joinedRef.current = true;
      setJoined(true);
      setPanelMode("compact");
    } catch (e) {
      stopLocalStream();
      setError(e?.name === "NotAllowedError" ? "Brak zgody na mikrofon." : "Nie udało się połączyć z kanałem głosowym.");
      setPanelMode("compact");
    } finally {
      setConnecting(false);
    }
  }

  async function handleAudioInputChange(event) {
    const nextDeviceId = event.target.value;
    rememberAudioInput(nextDeviceId);
    if (!joinedRef.current) return;
    setError("");
    try {
      await replaceLocalAudioInput(nextDeviceId);
    } catch (e) {
      setError(e?.name === "NotAllowedError" ? "Brak zgody na mikrofon." : "Nie udało się przełączyć mikrofonu.");
    }
  }

  async function applyAudioOutput(node, deviceId = selectedAudioOutputId) {
    if (!node || typeof node.setSinkId !== "function") return;
    try {
      await node.setSinkId(deviceId || "");
    } catch {
      setError("Nie udało się przełączyć wyjścia audio.");
    }
  }

  function handleAudioOutputChange(event) {
    const nextDeviceId = event.target.value;
    rememberAudioOutput(nextDeviceId);
    audioNodesRef.current.forEach((node) => applyAudioOutput(node, nextDeviceId));
  }

  function mergeChatMessage(message) {
    if (!message?.id) return;
    setChatMessages((current) => {
      if (current.some((item) => item.id === message.id)) return current;
      return [...current, message].slice(-300);
    });
  }

  const isOwnChatMessage = useCallback((message) => {
    return !!selfUserId && !!message?.userId && String(message.userId) === selfUserId;
  }, [selfUserId]);

  const markChatAsRead = useCallback(() => {
    const latestMessageTime = sortedChatMessages.reduce(
      (max, message) => Math.max(max, chatMessageTimeMs(message)),
      0
    );
    if (latestMessageTime > 0 && latestMessageTime > Number(lastChatReadAt || 0)) {
      writeStoredChatReadAt(channelIdRef.current, latestMessageTime);
      setLastChatReadAt(latestMessageTime);
    }
    setUnreadChatCount(0);
  }, [lastChatReadAt, sortedChatMessages]);

  async function sendChatMessage(event) {
    event?.preventDefault?.();
    const text = String(chatDraft || "").trim();
    if (!text || !socket?.connected || !channelIdRef.current) return;

    setChatDraft("");
    setChatError("");
    const response = await emitAck(socket, "VOICE:CHAT_SEND", {
      channelId: channelIdRef.current,
      text,
    });
    if (!response?.ok) {
      setChatDraft(text);
      setChatError("Nie udało się wysłać wiadomości.");
    }
  }

  function toggleMute() {
    const nextMuted = !muted;
    setMuted(nextMuted);
    mutedRef.current = nextMuted;
    applyOutboundAudioState();
    if (joinedRef.current) {
      socket.emit("VOICE:STATE", {
        channelId: channelIdRef.current,
        muted: nextMuted,
        deafened,
      });
    }
  }

  function toggleDeafen() {
    const nextDeafened = !deafened;
    setDeafened(nextDeafened);
    if (joinedRef.current) {
      socket.emit("VOICE:STATE", {
        channelId: channelIdRef.current,
        muted,
        deafened: nextDeafened,
      });
    }
  }

  useEffect(() => {
    if (!dockable || panelMode === "button") return;
    const panel = panelRef.current;
    if (!panel || typeof window === "undefined") return;
    const rect = panel.getBoundingClientRect();
    const bottomOverflow = rect.bottom - (window.innerHeight - 8);
    if (bottomOverflow > 0) {
      setDockOffset((current) => current + Math.ceil(bottomOverflow));
      return;
    }
    const topGap = rect.top - 8;
    if (topGap > 24 && dockOffset > 0) {
      setDockOffset((current) => Math.max(0, current - Math.min(Math.floor(topGap), 40)));
    }
  }, [dockOffset, dockable, expanded, panelMode, sortedChatMessages.length]);

  useEffect(() => {
    if (!joinedRef.current) return;
    socket?.emit("VOICE:STATE", {
      channelId,
      muted,
      deafened,
    });
  }, [channelId, deafened, muted, socket]);

  useEffect(() => {
    refreshAudioInputs();
    if (!navigator?.mediaDevices?.addEventListener) return undefined;
    const onDeviceChange = () => refreshAudioInputs();
    navigator.mediaDevices.addEventListener("devicechange", onDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener?.("devicechange", onDeviceChange);
    };
  }, []);

  useEffect(() => {
    audioNodesRef.current.forEach((node) => {
      if (!node) return;
      node.muted = deafened;
      node.volume = deafened ? 0 : localVoiceActive && antiEchoGateEnabled ? volume * DUCKING_VOLUME_FACTOR : volume;
    });
  }, [antiEchoGateEnabled, deafened, localVoiceActive, volume]);

  useEffect(() => {
    audioNodesRef.current.forEach((node) => applyAudioOutput(node));
  }, [selectedAudioOutputId]);

  useEffect(() => {
    if (!socket?.connected || !channelId) return undefined;
    let cancelled = false;

    async function loadChat() {
      setChatLoading(true);
      setChatError("");
      const response = await emitAck(socket, "VOICE:CHAT_SUBSCRIBE", { channelId }, 8000);
      if (cancelled) return;
      if (response?.ok) {
        setChatMessages(Array.isArray(response.messages) ? response.messages : []);
      } else {
        setChatError("Nie udało się pobrać czatu.");
      }
      setChatLoading(false);
    }

    loadChat();
    return () => {
      cancelled = true;
    };
  }, [channelId, socket, socket?.connected]);

  useEffect(() => {
    const node = chatListRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [sortedChatMessages.length, panelMode, expanded]);

  useEffect(() => {
    if (!channelId) return;
    if (panelMode !== "button") {
      markChatAsRead();
      return;
    }

    const nextUnreadCount = sortedChatMessages.filter(
      (message) => !isOwnChatMessage(message) && chatMessageTimeMs(message) > Number(lastChatReadAt || 0)
    ).length;
    setUnreadChatCount(nextUnreadCount);
  }, [channelId, isOwnChatMessage, lastChatReadAt, markChatAsRead, panelMode, sortedChatMessages]);

  useEffect(() => {
    if (!socket) return undefined;

    const onState = (payload) => {
      if (payload?.channelId !== channelIdRef.current) return;
      setParticipants(Array.isArray(payload.participants) ? payload.participants : []);
    };

    const onPeerJoined = async (payload) => {
      if (!joinedRef.current || payload?.channelId !== channelIdRef.current) return;
      const peerId = String(payload?.participant?.socketId || "");
      if (!peerId || peerId === socket.id) return;
      try {
        await createOfferFor(peerId);
      } catch {
        setError("Nie udało się zestawić połączenia głosowego.");
      }
    };

    const onOffer = async (payload) => {
      if (!joinedRef.current || payload?.channelId !== channelIdRef.current) return;
      const peerId = String(payload?.from || "");
      if (!peerId || !payload?.description) return;
      try {
        const pc = getOrCreatePeer(peerId);
        await pc.setRemoteDescription(new RTCSessionDescription(payload.description));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(enableOpusDtx(answer));
        socket.emit("VOICE:ANSWER", {
          channelId: channelIdRef.current,
          targetId: peerId,
          description: pc.localDescription,
        });
      } catch {
        setError("Nie udało się odebrać połączenia głosowego.");
      }
    };

    const onAnswer = async (payload) => {
      if (!joinedRef.current || payload?.channelId !== channelIdRef.current) return;
      const peerId = String(payload?.from || "");
      const pc = peersRef.current.get(peerId);
      if (!pc || !payload?.description) return;
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(payload.description));
      } catch {
        setError("Nie udało się zsynchronizować połączenia głosowego.");
      }
    };

    const onIce = async (payload) => {
      if (!joinedRef.current || payload?.channelId !== channelIdRef.current) return;
      const peerId = String(payload?.from || "");
      const pc = peersRef.current.get(peerId);
      if (!pc || !payload?.candidate) return;
      try {
        await pc.addIceCandidate(new RTCIceCandidate(payload.candidate));
      } catch {
        // ICE candidates can arrive during renegotiation; ignore single failures.
      }
    };

    const onPeerLeft = (payload) => {
      if (payload?.channelId !== channelIdRef.current) return;
      closePeer(String(payload?.peerId || ""));
    };

    const onChatMessage = (payload) => {
      if (payload?.channelId !== channelIdRef.current) return;
      mergeChatMessage(payload.message);
    };

    socket.on("VOICE:STATE", onState);
    socket.on("VOICE:PEER_JOINED", onPeerJoined);
    socket.on("VOICE:OFFER", onOffer);
    socket.on("VOICE:ANSWER", onAnswer);
    socket.on("VOICE:ICE", onIce);
    socket.on("VOICE:PEER_LEFT", onPeerLeft);
    socket.on("VOICE:CHAT_MESSAGE", onChatMessage);

    return () => {
      socket.off("VOICE:STATE", onState);
      socket.off("VOICE:PEER_JOINED", onPeerJoined);
      socket.off("VOICE:OFFER", onOffer);
      socket.off("VOICE:ANSWER", onAnswer);
      socket.off("VOICE:ICE", onIce);
      socket.off("VOICE:PEER_LEFT", onPeerLeft);
      socket.off("VOICE:CHAT_MESSAGE", onChatMessage);
    };
  }, [socket]);

  useEffect(() => () => {
    joinedRef.current = false;
    closeAllPeers();
    stopLocalStream();
    if (socket?.connected && channelIdRef.current) {
      socket.emit("VOICE:LEAVE", { channelId: channelIdRef.current });
    }
  }, [socket]);

  const audioProcessingInfo = [
    currentAudioSettings.echoCancellation === undefined ? null : `echo ${currentAudioSettings.echoCancellation ? "on" : "off"}`,
    currentAudioSettings.noiseSuppression === undefined ? null : `noise ${currentAudioSettings.noiseSuppression ? "on" : "off"}`,
    currentAudioSettings.autoGainControl === undefined ? null : `gain ${currentAudioSettings.autoGainControl ? "on" : "off"}`,
  ].filter(Boolean).join(" · ");
  const disabled = !channelId || !socket?.connected;
  const panelWidth = compact ? "min(360px, calc(100vw - 28px))" : 460;
  const panelMaxHeight = compact || dockable ? "calc(100dvh - 118px)" : undefined;
  const chatMaxHeight = expanded ? (compact ? 104 : 180) : (compact ? 96 : 112);
  const remoteAudioElements = Object.entries(remoteStreams).map(([peerId, stream]) => (
    <audio
      key={`voice-audio-${peerId}`}
      ref={(node) => {
        if (!node) {
          audioNodesRef.current.delete(peerId);
          return;
        }
        audioNodesRef.current.set(peerId, node);
        applyAudioOutput(node);
        node.volume = deafened ? 0 : localVoiceActive && antiEchoGateEnabled ? volume * DUCKING_VOLUME_FACTOR : volume;
        if (node.srcObject === stream) return;
        node.srcObject = stream;
      }}
      autoPlay
      playsInline
      muted={deafened}
      style={{ display: "none" }}
    />
  ));
  const buttonRow = joined ? (
    <>
      <IconButton title={muted ? "Włącz mikrofon" : "Wycisz mikrofon"} active={muted} onClick={toggleMute}>
        <IconMic off={muted} />
      </IconButton>
      <IconButton title={deafened ? "Włącz dźwięk" : "Wycisz dźwięk"} active={deafened} onClick={toggleDeafen}>
        <IconAudio off={deafened} />
      </IconButton>
      <IconButton title="Wyjdź z kanału" danger onClick={leaveChannel}>
        <IconPhone hangup />
      </IconButton>
    </>
  ) : (
    <IconButton title={connecting ? "Łączenie" : "Dołącz do głosu"} disabled={disabled || connecting} onClick={joinChannel}>
      <IconPhone />
    </IconButton>
  );

  if (panelMode === "button") {
    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "stretch",
          gap: 0,
          transform: dockable ? `translateY(-${dockOffset}px)` : "none",
        }}
      >
        <button
          type="button"
          className="btn btn-secondary"
          title={`${title}: ${statusText}`}
          aria-label={`${title}: ${statusText}`}
          onClick={() => setPanelMode("compact")}
          onPointerDown={stopControlPointerEvent}
          onTouchStart={stopControlPointerEvent}
          onContextMenu={(event) => event.preventDefault()}
          style={{
            position: "relative",
            width: 38,
            height: 38,
            minWidth: 38,
            padding: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 8,
            boxShadow: joined ? "0 0 18px rgba(56,189,248,0.24)" : "none",
            touchAction: "manipulation",
          }}
        >
          {joined ? <IconMic off={muted} /> : <IconPhone />}
          <span
            style={{
              position: "absolute",
              right: 5,
              top: 5,
              width: 8,
              height: 8,
              borderRadius: 999,
              background: joined ? (muted ? "#f59e0b" : "#22c55e") : connecting ? "#38bdf8" : "#64748b",
              boxShadow: joined && !muted ? `0 0 ${6 + micLevelPercent / 7}px rgba(34,197,94,0.75)` : "none",
            }}
          />
          {unreadChatCount > 0 ? (
            <span
              title={`${unreadBadgeLabel} nieodczytanych wiadomości`}
              aria-label={`${unreadBadgeLabel} nieodczytanych wiadomości`}
              style={{
                position: "absolute",
                right: -7,
                top: -7,
                minWidth: 18,
                height: 18,
                padding: "0 5px",
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box",
                background: "#f59e0b",
                color: "#111827",
                border: "1px solid rgba(255,255,255,0.92)",
                boxShadow: "0 3px 10px rgba(15,23,42,0.28)",
                fontSize: 10,
                fontWeight: 900,
                lineHeight: 1,
              }}
            >
              {unreadBadgeLabel}
            </span>
          ) : null}
          <span
            style={{
              position: "absolute",
              left: 5,
              right: 5,
              bottom: 4,
              height: 2,
              borderRadius: 999,
              background: "rgba(148,163,184,0.18)",
              overflow: "hidden",
            }}
          >
            <span
              style={{
                display: "block",
                width: `${muted || !joined ? 0 : micLevelPercent}%`,
                height: "100%",
                background: "#38bdf8",
              }}
            />
          </span>
        </button>
        {remoteAudioElements}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "stretch",
        gap: 0,
        maxWidth: "100%",
        transform: dockable ? `translateY(-${dockOffset}px)` : "none",
      }}
    >
      <div
        ref={panelRef}
        style={{
          display: "grid",
          gap: 8,
          width: panelWidth,
          minWidth: 0,
          maxWidth: "100%",
          maxHeight: panelMaxHeight,
          boxSizing: "border-box",
          overflowX: "hidden",
          overflowY: panelMaxHeight ? "auto" : "hidden",
          overscrollBehavior: "contain",
          padding: compact ? 6 : 8,
          border: `1px solid ${panelColors.border}`,
          borderRadius: 8,
          background: panelColors.panelBg,
          color: panelColors.panelText,
          boxShadow: joined ? "0 0 18px rgba(56,189,248,0.22)" : "none",
          backdropFilter: "blur(12px)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: 999,
              background: joined ? (muted ? "#f59e0b" : "#22c55e") : "#64748b",
              boxShadow: joined && !muted ? `0 0 ${8 + micLevelPercent / 5}px rgba(34,197,94,0.75)` : "none",
              flexShrink: 0,
            }}
          />
          <strong style={{ fontSize: 13, whiteSpace: "nowrap" }}>{title}</strong>
          <span style={{ fontSize: 12, color: panelColors.mutedText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {statusText}
          </span>
          <div style={{ flex: 1 }} />
          <div style={{ display: "inline-flex", gap: 5, alignItems: "center" }}>{buttonRow}</div>
          <IconButton
            title="Zwiń do przycisku"
            onClick={() => {
              setExpanded(false);
              setPanelMode("button");
            }}
          >
            <IconMinimize />
          </IconButton>
        </div>

        <div
          title={muted ? "Mikrofon wyciszony" : "Poziom mikrofonu"}
          style={{
            height: 3,
            borderRadius: 999,
            overflow: "hidden",
              background: isLightTheme ? "rgba(15,23,42,0.12)" : "rgba(148,163,184,0.18)",
          }}
        >
          <div
            style={{
              width: `${muted || !joined ? 0 : micLevelPercent}%`,
              height: "100%",
              borderRadius: 999,
              background: micLevelPercent > 55 ? "#22c55e" : "#38bdf8",
              transition: "width 80ms linear",
            }}
          />
        </div>

        <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, color: panelColors.faintText }}>
              Czat
            </span>
            <span style={{ fontSize: 11, color: panelColors.faintText }}>
              {chatLoading ? "ładowanie..." : `${sortedChatMessages.length} wiadomości`}
            </span>
          </div>
          <div
            ref={chatListRef}
            onPointerDown={stopControlPointerEvent}
            onPointerMove={stopControlPointerEvent}
            onPointerUp={stopControlPointerEvent}
            onTouchStart={stopControlPointerEvent}
            onTouchMove={stopControlPointerEvent}
            onContextMenu={stopControlPointerEvent}
            style={{
              display: "grid",
              alignContent: "start",
              gap: 5,
              minHeight: 68,
              maxHeight: chatMaxHeight,
              overflowY: "auto",
              padding: 7,
              borderRadius: 8,
              border: `1px solid ${panelColors.softBorder}`,
              background: panelColors.chatBg,
              userSelect: "text",
              WebkitUserSelect: "text",
              WebkitTouchCallout: "default",
              touchAction: "pan-y",
              cursor: "text",
            }}
          >
            {sortedChatMessages.length ? (
              sortedChatMessages.map((message) => (
                <div key={message.id} style={{ display: "grid", gap: 2, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "baseline", minWidth: 0 }}>
                    <strong style={{ fontSize: 11, color: panelColors.strong, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {message.name || "Uczestnik"}
                    </strong>
                    <span
                      title={formatChatTimestampTitle(message.createdAt)}
                      style={{ fontSize: 10, color: panelColors.faintText, flexShrink: 0 }}
                    >
                      {formatChatTime(message.createdAt)}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      lineHeight: 1.35,
                      color: panelColors.panelText,
                      overflowWrap: "anywhere",
                      userSelect: "text",
                      WebkitUserSelect: "text",
                    }}
                  >
                    {message.text}
                  </div>
                </div>
              ))
            ) : (
              <span style={{ fontSize: 12, color: panelColors.faintText }}>
                Brak wiadomości.
              </span>
            )}
          </div>
          <form onSubmit={sendChatMessage} style={{ display: "flex", gap: 6, minWidth: 0 }}>
            <input
              type="text"
              value={chatDraft}
              maxLength={MAX_CHAT_DRAFT_LENGTH}
              onChange={(event) => setChatDraft(event.target.value.slice(0, MAX_CHAT_DRAFT_LENGTH))}
              placeholder="Napisz wiadomość..."
              aria-label="Wiadomość na czacie"
              style={{
                ...themedInputSelectStyle,
                minHeight: 30,
                height: 30,
                flex: 1,
                minWidth: 0,
                maxWidth: "100%",
                boxSizing: "border-box",
                padding: "4px 8px",
              }}
            />
            <button
              type="submit"
              className="btn btn-secondary"
              disabled={!chatDraft.trim() || !socket?.connected}
              style={{ minHeight: 30, padding: "4px 9px", fontSize: 12, flexShrink: 0 }}
            >
              Wyślij
            </button>
          </form>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <button
              type="button"
              className="btn btn-secondary"
              title={expanded ? "Zwiń ustawienia" : "Rozwiń ustawienia"}
              aria-label={expanded ? "Zwiń ustawienia" : "Rozwiń ustawienia"}
              onClick={() => setExpanded((open) => !open)}
              style={{
                minHeight: 28,
                width: 32,
                minWidth: 32,
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconGear />
            </button>
            <span style={{ fontSize: 11, color: panelColors.faintText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {expanded ? "Ustawienia audio" : currentMicLabel || (joined ? "Mikrofon aktywny" : "Ustawienia")}
            </span>
          </div>
          {chatError ? <span style={{ fontSize: 11, color: panelColors.error }}>{chatError}</span> : null}
        </div>

        {expanded ? (
          <div style={{ display: "grid", gap: 8, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
              <span style={{ fontSize: 12, color: panelColors.mutedText }}>Głośność</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={(event) => setVolume(Number(event.target.value))}
                style={{ width: compact ? 92 : 100, minWidth: 72, flex: compact ? "1 1 92px" : "0 0 100px" }}
              />
              <strong style={{ minWidth: 38, fontSize: 12, textAlign: "right" }}>{volumePercent}%</strong>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={refreshAudioInputs}
                title="Odśwież urządzenia audio"
                aria-label="Odśwież urządzenia audio"
                style={{ minHeight: 28, maxWidth: 86, padding: "3px 8px", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis" }}
              >
                Odśwież
              </button>
            </div>

            <div style={{ display: "grid", gap: 5, minWidth: 0 }}>
              <div style={{ display: "grid", minWidth: 0 }}>
                <select
                  value={selectedAudioInputId}
                  onChange={handleAudioInputChange}
                  onFocus={refreshAudioInputs}
                  title="Mikrofon"
                  aria-label="Mikrofon"
                  style={themedInputSelectStyle}
                >
                  <option value="">Domyślny mikrofon</option>
                  {audioInputs.map((device, index) => (
                    <option key={device.deviceId || `audio-input-${index}`} value={device.deviceId}>
                      {device.label || `Mikrofon ${index + 1}`}
                    </option>
                  ))}
                </select>
              </div>
              {audioOutputs.length > 0 ? (
                <select
                  value={selectedAudioOutputId}
                  onChange={handleAudioOutputChange}
                  onFocus={refreshAudioInputs}
                  title="Wyjście audio"
                  aria-label="Wyjście audio"
                  style={themedInputSelectStyle}
                >
                  <option value="">Domyślne wyjście audio</option>
                  {audioOutputs.map((device, index) => (
                    <option key={device.deviceId || `audio-output-${index}`} value={device.deviceId}>
                      {device.label || `Wyjście ${index + 1}`}
                    </option>
                  ))}
                </select>
              ) : null}
              <div
                style={{
                  ...activeMicInfoStyle,
                  color: isLightTheme ? "rgba(51,65,85,0.72)" : activeMicInfoStyle.color,
                }}
                title={`${currentMicLabel || ""}${audioProcessingInfo ? ` · ${audioProcessingInfo}` : ""}`}
              >
                Aktywny: {currentMicLabel || (joined ? "wykrywanie..." : "po dołączeniu")}
                {audioProcessingInfo ? ` · ${audioProcessingInfo}` : ""}
              </div>
            </div>

            <div style={{ display: "grid", gap: 4 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, color: panelColors.faintText }}>
                Na kanale
              </div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {joined ? (
                  <>
                    <span style={themedParticipantPillStyle}>
                      {selfName}{muted ? " · mic off" : ""}
                    </span>
                    {remoteParticipants.map((participant) => (
                      <span key={participant.socketId} style={themedParticipantPillStyle}>
                        {getDisplayName(participant)}{participant.muted ? " · mic off" : ""}
                      </span>
                    ))}
                  </>
                ) : (
                  <span style={{ fontSize: 12, color: panelColors.faintText }}>Dołącz, żeby rozpocząć rozmowę.</span>
                )}
              </div>
            </div>

            {error ? <span style={{ fontSize: 12, color: panelColors.error }}>{error}</span> : null}
          </div>
        ) : error ? (
          <span style={{ fontSize: 12, color: panelColors.error }}>{error}</span>
        ) : null}
      </div>
      {remoteAudioElements}
    </div>
  );
}

const participantPillStyle = {
  display: "inline-flex",
  alignItems: "center",
  minHeight: 24,
  padding: "3px 8px",
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "rgba(15,23,42,0.68)",
  fontSize: 12,
  color: "rgba(248,250,252,0.9)",
};

function getParticipantPillStyle(isLightTheme) {
  return {
    ...participantPillStyle,
    border: isLightTheme ? "1px solid rgba(15,23,42,0.16)" : participantPillStyle.border,
    background: isLightTheme ? "rgba(226,232,240,0.78)" : participantPillStyle.background,
    color: isLightTheme ? "#0f172a" : participantPillStyle.color,
  };
}

function getInputSelectStyle(isLightTheme) {
  return {
    ...inputSelectStyle,
    border: isLightTheme ? "1px solid rgba(15,23,42,0.2)" : inputSelectStyle.border,
    background: isLightTheme ? "rgba(248,250,252,0.96)" : inputSelectStyle.background,
    color: isLightTheme ? "#0f172a" : inputSelectStyle.color,
  };
}

const inputSelectStyle = {
  minWidth: 0,
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  height: 30,
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.38)",
  background: "rgba(15,23,42,0.92)",
  color: "#f8fafc",
  fontSize: 12,
  padding: "0 8px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const activeMicInfoStyle = {
  minWidth: 0,
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: 11,
  color: "rgba(226,232,240,0.66)",
  lineHeight: 1.35,
};
